// ─────────────────────────────────────────
// ts/nav.ts — Navegação principal
// Monta o menu a partir da tradução, controla o estado ativo
// por página, o botão de CTA e o menu sanfona no mobile.
// Tauvior — Hardware, sistemas embarcados e conectividade industrial
// ─────────────────────────────────────────

import { getTranslation, withLocaleParam } from '../i18n/index';
import { scrollToSection } from './utils';
import { mountThemeToggle } from './theme';

const NAV_SCROLLED_CLASS = 'scrolled';
const NAV_OPEN_CLASS     = 'nav-open';
const SCROLL_THRESHOLD   = 20; // px

/** Escapa texto antes de interpolar em template de innerHTML. */
function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Rotas ──────────────────────────────────

/**
 * Normaliza um caminho para comparação: `/grid.html`, `/grid/` e `/grid`
 * são a mesma página. O `.html` aparece no dev server; a Vercel serve sem
 * ele (cleanUrls).
 */
function normalizePath(path: string): string {
  const clean = path
    .replace(/\.html$/, '')
    .replace(/\/index$/, '')
    .replace(/\/+$/, '');
  return clean === '' ? '/' : clean;
}

function isCurrentPath(href: string): boolean {
  const target = normalizePath(new URL(href, window.location.origin).pathname);
  return target === normalizePath(window.location.pathname);
}

// ── Construção do menu ─────────────────────

/**
 * O menu é escrito aqui, e não no HTML, porque todas as páginas servem a
 * mesma navegação — duplicar a marcação em cada uma é o caminho curto para
 * elas divergirem.
 *
 * A ordem segue a arquitetura da marca: a Tauvior (Home), as soluções
 * (submenu com Grid e Advisory) e, por fim, Sobre e Contato.
 */
function buildNavLinks(nav: HTMLElement): HTMLElement | null {
  const t = getTranslation().nav;

  const container = nav.querySelector<HTMLElement>('.nav-links');
  if (!container) return null;

  const link = (href: string, label: string, cls = 'nav-item nav-link'): string =>
    `<a class="${cls}" href="${esc(withLocaleParam(href))}">${esc(label)}</a>`;

  // Soluções: botão que abre o submenu (hover/clique no desktop, sanfona no mobile)
  const solutions = `
    <div class="nav-dropdown">
      <button type="button" class="nav-item nav-dropdown-toggle" aria-expanded="false" aria-controls="nav-solutions-menu">
        ${esc(t.solutions)}<svg class="nav-dropdown-chevron" viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1l4 4 4-4"/></svg>
      </button>
      <div class="nav-dropdown-menu" id="nav-solutions-menu">
        ${link('/grid', t.grid, 'nav-link nav-dropdown-link')}
        ${link('/advisory', t.advisory, 'nav-link nav-dropdown-link')}
      </div>
    </div>`;

  container.id = 'nav-links-list';
  container.setAttribute('aria-label', t.menuLabel);
  nav.querySelector('.logo')?.setAttribute('aria-label', t.logoLabel);
  nav.querySelector('#lang-switcher')?.setAttribute('aria-label', t.langLabel);
  container.innerHTML = `
    ${link('/', t.home)}
    ${solutions}
    ${link('/sobre', t.sobre)}
    ${link('/contato', t.contact)}
  `;

  return container;
}

// ── Estado ativo ───────────────────────────

function markActiveLinks(container: HTMLElement): void {
  container.querySelectorAll<HTMLAnchorElement>('.nav-link').forEach((link) => {
    if (link.hash || !isCurrentPath(link.href)) return;
    link.classList.add('is-active');
    link.setAttribute('aria-current', 'page');
    // Página dentro do submenu: o "Soluções" também acende
    link.closest('.nav-dropdown')?.querySelector('.nav-dropdown-toggle')?.classList.add('is-active');
  });
}

// ── Submenu Soluções ───────────────────────

function initDropdowns(container: HTMLElement): void {
  container.querySelectorAll<HTMLElement>('.nav-dropdown').forEach((dropdown) => {
    const toggle = dropdown.querySelector<HTMLButtonElement>('.nav-dropdown-toggle');
    if (!toggle) return;
    const setOpen = (open: boolean): void => {
      dropdown.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
    };
    toggle.addEventListener('click', () => setOpen(!dropdown.classList.contains('is-open')));
    dropdown.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !dropdown.classList.contains('is-open')) return;
      e.stopPropagation();
      setOpen(false);
      toggle.focus();
    });
    // Fecha ao sair com o foco ou clicar fora
    dropdown.addEventListener('focusout', (e) => {
      if (!dropdown.contains(e.relatedTarget as Node | null)) setOpen(false);
    });
    document.addEventListener('click', (e) => {
      if (!dropdown.contains(e.target as Node)) setOpen(false);
    });
  });
}

// ── Scroll opacity ─────────────────────────

function handleNavScroll(nav: HTMLElement): void {
  nav.classList.toggle(NAV_SCROLLED_CLASS, window.scrollY > SCROLL_THRESHOLD);
  syncNavTone(nav);
}

/**
 * Quando a barra está sobre uma seção inversa (escura no tema claro), ela
 * assume o mesmo `data-tone="inverse"`: os tokens semânticos (fundo, texto,
 * borda e `--logo-filter`) se re-derivam sozinhos e a costura entre barra e
 * seção some, com o logo sempre contrastando. Não vale com o menu aberto,
 * porque o painel sanfona tem fundo próprio.
 */
function syncNavTone(nav: HTMLElement): void {
  const probe = nav.offsetHeight / 2;
  const over = Array.from(
    document.querySelectorAll<HTMLElement>('[data-tone="inverse"]'),
  ).some((el) => {
    if (nav.contains(el)) return false;
    const r = el.getBoundingClientRect();
    return r.top <= probe && r.bottom > probe;
  });
  if (over) nav.setAttribute('data-tone', 'inverse');
  else nav.removeAttribute('data-tone');
}

// ── CTA nav button ─────────────────────────

/**
 * O CTA rola até o formulário quando ele está na página; nas páginas sem
 * formulário leva para /contato.
 */
function initNavCta(): void {
  document.getElementById('nav-cta-btn')?.addEventListener('click', () => {
    closeMenu();
    if (document.getElementById('contato')) {
      scrollToSection({ targetId: 'contato' });
    } else {
      window.location.href = withLocaleParam('/contato');
    }
  });
}

// ── Mobile hamburger menu ──────────────────

let menuOpen = false;

function setMenuLabel(open: boolean): void {
  const t = getTranslation().nav;
  document.getElementById('hamburger')?.setAttribute('aria-label', open ? t.menuClose : t.menuOpen);
}

function openMenu(): void {
  menuOpen = true;
  setMenuLabel(true);
  document.body.classList.add(NAV_OPEN_CLASS);
  document.getElementById('main-nav')?.classList.add(NAV_OPEN_CLASS);
  document.getElementById('hamburger')?.setAttribute('aria-expanded', 'true');
}

function closeMenu(): void {
  menuOpen = false;
  setMenuLabel(false);
  document.body.classList.remove(NAV_OPEN_CLASS);
  document.getElementById('main-nav')?.classList.remove(NAV_OPEN_CLASS);
  document.getElementById('hamburger')?.setAttribute('aria-expanded', 'false');
}

function toggleMenu(): void {
  menuOpen ? closeMenu() : openMenu();
}

function injectHamburger(nav: HTMLElement, container: HTMLElement): void {
  const btn = document.createElement('button');
  btn.id        = 'hamburger';
  btn.className = 'hamburger';
  btn.setAttribute('aria-label',    getTranslation().nav.menuOpen);
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-controls', container.id);
  btn.innerHTML = `
    <span class="ham-line"></span>
    <span class="ham-line"></span>
    <span class="ham-line"></span>
  `;

  btn.addEventListener('click', toggleMenu);
  nav.appendChild(btn);

  // Fecha o menu ao clicar em um link
  container.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', closeMenu);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menuOpen) closeMenu();
  });

  document.addEventListener('click', (e) => {
    if (nav.contains(e.target as Node)) return;
    if (menuOpen) closeMenu();
  });
}

// ── Botão de tema ──────────────────────────

/**
 * Fica ao lado do seletor de idioma, dentro de `.nav-right` — que continua
 * visível no mobile, ao lado do hambúrguer, então o botão não precisa de
 * uma segunda cópia dentro do menu sanfona.
 */
function initThemeToggle(nav: HTMLElement): void {
  const right = nav.querySelector<HTMLElement>('.nav-right');
  if (!right) return;
  mountThemeToggle(right, right.querySelector('#lang-switcher'));
}

// ── Public init ────────────────────────────

export function initNav(): void {
  const nav = document.getElementById('main-nav');
  if (!nav) return;

  const container = buildNavLinks(nav);
  if (!container) return;

  markActiveLinks(container);
  initDropdowns(container);

  let ticking = false;
  const onScroll = (): void => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      handleNavScroll(nav);
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  handleNavScroll(nav);

  initNavCta();
  initThemeToggle(nav);
  injectHamburger(nav, container);
}
